/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file totoSlice.js
 * @description Redux slice for managing TOTO games related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the TOTO slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = () => ({
  currentDraws: [],
  currentDrawsLoading: false,
  drawFinancials: {},
  // { [drawId]: { [currency]: financialData } }
drawFinancialsLoading: false,

error: null,

gameDetails: {},

gameDetailsLoading: false,

betSubmissionError: null,
  games: [],
  betSubmissionErrorCode: null,

gamesLoading: false,


// Bet slip state for multi-grid management
betSlip: {
    drawId: null,
    gameId: null,
    currencyCode: null,
    grids: {},

isValid: false,
    // { [gridId]: { gridId, gridNumber, gridName, mandatory, prizePercentage, selections: {}, isComplete: false } }
totalStake: null,
    validationErrors: [],
  },



previewSelections: {},



betSubmissionLoading: false,



selectedCurrency: null,



lastSubmittedBet: null,


// { [drawId]: { [eventId]: { [gridId]: { column_0: ["1", "X"], column_1: ["Over 2.5"] } } } }
selectedDrawId: null,


selectedGameId: null,


selectedStake: null,

  // { [eventId]: { [gridId]: { column_0: ["1", "X"], column_1: ["Over 2.5"], addon_column_0: ["GG"] } } }
selectionsByDraw: {},

  // Multi-line betting support (for PROMO 13 and other multi-line games)
  betslipLines: [],
  // Array of line objects: [{ lineNumber: 0, selections: { [eventId]: { [gridId]: { column_0: [...] } } } }]
  currentLineIndex: 0,
  // Currently active line being edited
  requiredLineCount: 1,
  // Number of lines required by the game (from game.requiredBetslipLines)
  isMultiLineMode: false,
  // Flag indicating if current game uses multi-line betting
});

/**
 * @function getTotoGames
 * @description Async thunk for fetching available TOTO games list
 * @param {Object} params - Query parameters (status, type, featured, includeJackpot)
 * @returns {Promise<Object>} Result of the operation
 */
export const getTotoGames = createAsyncThunk(
  "toto/getTotoGames",
  async ({ featured, includeJackpot, status, type } = {}, thunkAPI) => {
    try {
      const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
      const axios = createAxiosInstance(thunkAPI.dispatch, {
        authToken,
        language,
      });

      // Build query parameters
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (type) params.append("type", type);
      if (featured !== undefined) params.append("featured", featured);
      if (includeJackpot !== undefined) params.append("includeJackpot", includeJackpot);
      params.append("originId", originId);
      params.append("lineId", lineId);

      const result = await axios.get(`/player/toto/games?${params.toString()}`);

      // API returns {games: [...]}
      return {
        games: result.data?.games || [],
      };
    } catch (err) {
      const customError = {
        message: err.response?.headers["x-information"] || "Unable to fetch TOTO games",
        name: "TOTO Games Fetch Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  }
);

/**
 * @function getTotoGameDetails
 * @description Async thunk for fetching detailed TOTO game information
 * @param {string} gameId - The game ID
 * @returns {Promise<Object>} Result of the operation
 */
export const getTotoGameDetails = createAsyncThunk(
  "toto/getTotoGameDetails",
  async ({ gameId }, thunkAPI) => {
    try {
      const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
      const axios = createAxiosInstance(thunkAPI.dispatch, {
        authToken,
        language,
      });

      const result = await axios.get(
        `/player/toto/games/${gameId}?originId=${originId}&lineId=${lineId}`
      );

      return {
        gameDetails: result.data,
        gameId,
      };
    } catch (err) {
      const customError = {
        message: err.response?.headers["x-information"] || `Unable to fetch game details for ${gameId}`,
        name: "TOTO Game Details Fetch Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  }
);

/**
 * @function getTotoCurrentDraws
 * @description Async thunk for fetching currently open TOTO draws
 * @param {Object} params - Query parameters (gameType, includeProgram)
 * @returns {Promise<Object>} Result of the operation
 */
export const getTotoCurrentDraws = createAsyncThunk(
  "toto/getTotoCurrentDraws",
  async ({ gameType, includeProgram } = {}, thunkAPI) => {
    try {
      const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
      const axios = createAxiosInstance(thunkAPI.dispatch, {
        authToken,
        language,
      });

      // Build query parameters
      const params = new URLSearchParams();
      if (gameType) params.append("gameType", gameType);
      if (includeProgram !== undefined) params.append("includeProgram", includeProgram);
      params.append("originId", originId);
      params.append("lineId", lineId);

      const result = await axios.get(`/player/toto/draws/current?${params.toString()}`);

      // API returns {draws: [...]}
      return {
        currentDraws: result.data?.draws || [],
      };
    } catch (err) {
      const customError = {
        message: err.response?.headers["x-information"] || "Unable to fetch current TOTO draws",
        name: "TOTO Current Draws Fetch Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  }
);

/**
 * @function getTotoDrawFinancials
 * @description Async thunk for fetching financial data for a specific draw and currency
 * @param {Object} params - Query parameters (drawId, currency)
 * @returns {Promise<Object>} Result of the operation
 */
export const getTotoDrawFinancials = createAsyncThunk(
  "toto/getTotoDrawFinancials",
  async ({ currency, drawId }, thunkAPI) => {
    try {
      const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
      const axios = createAxiosInstance(thunkAPI.dispatch, {
        authToken,
        language,
      });

      // Build query parameters
      const params = new URLSearchParams();
      params.append("currency", currency);
      params.append("originId", originId);
      params.append("lineId", lineId);

      const result = await axios.get(
        `/player/toto/draws/${drawId}/financials?${params.toString()}`
      );

      return {
        currency,
        drawId,
        financials: result.data,
      };
    } catch (err) {
      const customError = {
        message: err.response?.headers["x-information"] || `Unable to fetch financial data for draw ${drawId}`,
        name: "TOTO Draw Financials Fetch Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  }
);

/**
 * @function submitTotoBet
 * @description Async thunk for submitting a TOTO bet
 * @param {Object} params - Bet submission parameters
 * @returns {Promise<Object>} Result of the operation
 */
export const submitTotoBet = createAsyncThunk(
  "toto/submitTotoBet",
  async ({ currencyCode, drawId, gameId, selections, stake }, thunkAPI) => {
    try {
      const { accountId, authToken, lineId, originId } = getRequestParams(thunkAPI.getState());
      const axios = createAxiosInstance(thunkAPI.dispatch, {
        authToken,
      });

      const state = thunkAPI.getState();
      const isMultiLineMode = state.toto?.isMultiLineMode || false;
      const betslipLines = state.toto?.betslipLines || [];
      const requiredLineCount = state.toto?.requiredLineCount || 1;

      // Get game configuration to determine grid structure
      const gameDetails = state.toto?.gameDetails?.[gameId];
      const gameGrids = gameDetails?.grids || [];

      // Debug logging
      console.log('=== TOTO BET SUBMISSION DEBUG ===');
      console.log('gameId:', gameId);
      console.log('gameDetails exists?:', !!gameDetails);
      if (gameDetails) {
        console.log('gameDetails.grids exists?:', !!gameDetails.grids);
        console.log('gameDetails.grids:', gameDetails.grids);
      } else {
        console.log('Available game IDs in state:', Object.keys(state.toto?.gameDetails || {}));
      }
      console.log('gameGrids count:', gameGrids.length);
      console.log('isMultiLineMode:', isMultiLineMode);
      console.log('betslipLines count:', betslipLines.length);
      console.log('requiredLineCount:', requiredLineCount);
      console.log('================================');

      // Transform selections to NEW API specification (grids.lines format)
      // Output format:
      // {
      //   grids: {
      //     [gridId]: {
      //       lines: [
      //         {
      //           column1Selections: [{eventId, optionCode}],
      //           column2Selections: [{eventId, optionCode}],
      //           addonColumn1Selections: [{eventId, optionCode}],
      //           addonColumn2Selections: [{eventId, optionCode}]
      //         },
      //         // ... more lines for multi-line games
      //       ]
      //     }
      //   }
      // }

      const buildLineFromSelections = (lineSelections, gridId) => {
        const line = {
          column1Selections: [],
          column2Selections: [],
          addonColumn1Selections: [],
          addonColumn2Selections: []
        };

        Object.entries(lineSelections).forEach(([eventId, eventSelections]) => {
          Object.entries(eventSelections).forEach(([grid, gridEventSelections]) => {
            // Check if this is the grid we're building for, or if it's addon
            const isAddonGrid = grid === 'addon';
            const isTargetGrid = String(grid) === String(gridId);

            if (!isAddonGrid && !isTargetGrid) return;

            // Process all columns for this event
            Object.entries(gridEventSelections).forEach(([columnKey, columnSelections]) => {
              if (columnSelections && columnSelections.length > 0) {
                // Extract column number from key
                const columnMatch = columnKey.match(/(\d+)$/);
                if (!columnMatch) return;

                const columnNumber = parseInt(columnMatch[1]) + 1; // Convert 0-based to 1-based

                // Determine field name based on whether it's addon
                const fieldName = isAddonGrid
                  ? `addonColumn${columnNumber}Selections`
                  : `column${columnNumber}Selections`;

                // Add ALL selections for this event to the appropriate column array
                columnSelections.forEach(optionCode => {
                  line[fieldName].push({
                    eventId: parseInt(eventId),
                    optionCode
                  });
                });
              }
            });
          });
        });

        return line;
      };

      const grids = {};

      if (isMultiLineMode && betslipLines.length > 0) {
        // MULTI-LINE MODE: Build lines array for each grid
        console.log('TOTO Multi-Line Bet Submission - Lines:', betslipLines.length);

        // Validate line count
        if (betslipLines.length !== requiredLineCount) {
          throw new Error(`Game requires exactly ${requiredLineCount} bet lines. You provided ${betslipLines.length} lines.`);
        }

        gameGrids.forEach((grid, index) => {
          console.log(`Processing grid ${index + 1}/${gameGrids.length}: gridId=${grid.id}, gridName=${grid.gridName || grid.name}`);

          const linesForGrid = betslipLines.map((lineObj, lineIndex) => {
            const lineData = buildLineFromSelections(lineObj.selections, grid.id);
            console.log(`  Line ${lineIndex + 1}: column1=${lineData.column1Selections.length} selections`);
            return lineData;
          });

          grids[grid.id] = { lines: linesForGrid };
          console.log(`Grid ${grid.id} built with ${linesForGrid.length} lines`);
        });
      } else {
        // SINGLE-LINE MODE: Convert single line to lines array format
        console.log('TOTO Single-Line Bet Submission');

        gameGrids.forEach((grid, index) => {
          console.log(`Processing grid ${index + 1}/${gameGrids.length}: gridId=${grid.id}`);

          const lineData = buildLineFromSelections(selections, grid.id);
          console.log(`  Single line: column1=${lineData.column1Selections.length} selections`);

          grids[grid.id] = { lines: [lineData] };
        });
      }

      // Build the final bet request with new grids.lines format
      const betData = {
        drawId: parseInt(drawId),
        grids,  // NEW: grids with lines array
        originId: parseInt(originId),
        stakePerCombination: parseFloat(stake)
      };

      // Debug logging
      console.log('TOTO Bet Submission - Final payload (new format):', JSON.stringify(betData, null, 2));

      const result = await axios.post(`/player/acc/${accountId}/toto/bets?originId=${parseInt(originId)}`, betData);

      return {
        betId: result.data?.betId,
        drawId: result.data?.drawId,
        gridSelections: result.data?.gridSelections || result.data?.grids || {},
        message: result.data?.message || "Bet placed successfully",
        potentialWinnings: result.data?.potentialWinnings,
        stakePerCombination: result.data?.stakePerCombination || result.data?.totalStake,
        success: result.data?.status === "ACCEPTED",
      };
    } catch (err) {
      // Handle specific TOTO error codes
      const errorCode = err.response?.data?.code;
      const errorMessage = err.response?.data?.descriptionI18n ||
                          err.response?.data?.description ||
                          err.response?.data?.error ||
                          err.response?.headers["x-information"] ||
                          "Unable to submit TOTO bet";

      const customError = {
        code: errorCode || err.response?.status,
        message: errorMessage,
        name: "TOTO Bet Submission Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  }
);

/**
 * @function checkGridCompletion
 * @description Check if a grid is complete based on its selections
 * @param {Object} gridState - Grid state object
 * @returns {boolean} True if grid is complete
 */
const checkGridCompletion = (gridState) => {
  if (!gridState.mandatory) {
    // Optional grids are considered complete if they have any selections
    return Object.keys(gridState.selections).length > 0;
  }

  // Mandatory grids need at least one selection per required event
  // This is a simplified check - actual validation should use game configuration
  return Object.keys(gridState.selections).length >= Math.min(3, gridState.eventCount || 3);
};

/**
 * @function updateBetSlipValidation
 * @description Update bet slip validation status
 * @param {Object} state - Redux state
 */
const updateBetSlipValidation = (state) => {
  const errors = [];

  // Check if bet slip has any selections at all
  const hasAnySelections = Object.values(state.betSlip.grids).some(grid =>
    Object.keys(grid.selections).length > 0
  );

  if (!hasAnySelections) {
    errors.push("Please make at least one selection to place a bet");
  } else {
    // Basic validation for grids with selections
    Object.values(state.betSlip.grids).forEach(grid => {
      const hasGridSelections = Object.keys(grid.selections).length > 0;

      if (hasGridSelections) {
        // Check if grid has enough selections (basic check)
        const selectionCount = Object.keys(grid.selections).length;
        const expectedEventCount = grid.eventCount || 0;

        if (selectionCount < expectedEventCount && !grid.mandatory) {
          // For optional grids, warn if incomplete but allow
          errors.push(`${grid.gridName}: Consider completing all ${expectedEventCount} events for better chances`);
        } else if (selectionCount < expectedEventCount && grid.mandatory) {
          // For mandatory grids, require completion
          const remaining = expectedEventCount - selectionCount;
          errors.push(`${grid.gridName}: Please complete ${remaining} more event${remaining > 1 ? 's' : ''}`);
        }
      }
    });

    // Check if all mandatory grids have selections
    const mandatoryGrids = Object.values(state.betSlip.grids).filter(grid => grid.mandatory);
    mandatoryGrids.forEach(grid => {
      if (Object.keys(grid.selections).length === 0) {
        errors.push(`${grid.gridName} is mandatory and requires complete selections`);
      }
    });
  }

  // Check stake
  if (!state.betSlip.totalStake || state.betSlip.totalStake <= 0) {
    errors.push("Please enter a valid stake amount");
  }

  state.betSlip.validationErrors = errors;
  state.betSlip.isValid = errors.length === 0 && hasAnySelections;
};

/**
 * @constant totoSlice
 * @description Redux slice for TOTO games management
 */
const totoSlice = createSlice({
  extraReducers: (builder) => {
    builder
      // Handle getTotoGames
      .addCase(getTotoGames.pending, (state) => {
        state.error = null;
        state.gamesLoading = true;
      })
      .addCase(getTotoGames.rejected, (state, action) => {
        state.error = action.error.message;
        state.gamesLoading = false;
      })
      .addCase(getTotoGames.fulfilled, (state, action) => {
        state.games = action.payload.games;
        state.gamesLoading = false;
        state.error = null;
      })
      // Handle getTotoGameDetails
      .addCase(getTotoGameDetails.pending, (state) => {
        state.error = null;
        state.gameDetailsLoading = true;
      })
      .addCase(getTotoGameDetails.rejected, (state, action) => {
        state.error = action.error.message;
        state.gameDetailsLoading = false;
      })
      .addCase(getTotoGameDetails.fulfilled, (state, action) => {
        state.gameDetails[action.payload.gameId] = action.payload.gameDetails;
        state.gameDetailsLoading = false;
        state.error = null;
      })
      // Handle getTotoCurrentDraws
      .addCase(getTotoCurrentDraws.pending, (state) => {
        state.error = null;
        state.currentDrawsLoading = true;
      })
      .addCase(getTotoCurrentDraws.rejected, (state, action) => {
        state.error = action.error.message;
        state.currentDrawsLoading = false;
      })
      .addCase(getTotoCurrentDraws.fulfilled, (state, action) => {
        const newDraws = action.payload.currentDraws;

        // Create a comparison key based on draw IDs and their modification times
        const getDrawsKey = (draws) => {
          if (!draws || !draws.length) return '';
          
return draws.map(d => `${d.drawId || d.id}-${d.modifiedTime || d.updateTime || ''}`).join(',');
        };

        const currentKey = getDrawsKey(state.currentDraws);
        const newKey = getDrawsKey(newDraws);

        // Only update if draws have actually changed
        if (currentKey !== newKey) {
          state.currentDraws = newDraws;
        }

        state.currentDrawsLoading = false;
        state.error = null;
      })
      // Handle getTotoDrawFinancials
      .addCase(getTotoDrawFinancials.pending, (state) => {
        state.drawFinancialsLoading = true;
      })
      .addCase(getTotoDrawFinancials.rejected, (state, action) => {
        state.drawFinancialsLoading = false;
        state.error = action.error.message;
      })
      .addCase(getTotoDrawFinancials.fulfilled, (state, action) => {
        const { currency, drawId, financials } = action.payload;
        if (!state.drawFinancials[drawId]) {
          state.drawFinancials[drawId] = {};
        }
        state.drawFinancials[drawId][currency] = financials;
        state.drawFinancialsLoading = false;
      })
      // Handle submitTotoBet
      .addCase(submitTotoBet.pending, (state) => {
        state.betSubmissionLoading = true;
        state.betSubmissionError = null;
      })
      .addCase(submitTotoBet.rejected, (state, action) => {
        state.betSubmissionLoading = false;
        state.betSubmissionError = action.error.message;
        state.betSubmissionErrorCode = action.error.code;
      })
      .addCase(submitTotoBet.fulfilled, (state, action) => {
        state.betSubmissionLoading = false;
        state.betSubmissionError = null;
        state.betSubmissionErrorCode = null;
        state.lastSubmittedBet = action.payload;

        // Clear all selections after successful submission (both single-line and multi-line)
        state.previewSelections = {};
        state.selectionsByDraw = {};

        // Clear multi-line selections
        if (state.betslipLines.length > 0) {
          state.betslipLines = state.betslipLines.map(line => ({
            ...line,
            selections: {}
          }));
        }
      });
  },
  initialState: getInitialState(),
  name: "toto",
  reducers: {
    /**
     * @function clearTotoError
     * @description Clear any error in the TOTO state
     */
    clearTotoError: (state) => {
      state.error = null;
    },
    
    /**
     * @function clearBetSubmissionError
     * @description Clear bet submission error
     */
clearBetSubmissionError: (state) => {
      state.betSubmissionError = null;
    },
    
    

/**
     * @function clearTotoGameDetails
     * @description Clear game details for a specific game or all games
     */
clearTotoGameDetails: (state, action) => {
      if (action.payload?.gameId) {
        delete state.gameDetails[action.payload.gameId];
      } else {
        state.gameDetails = {};
      }
    },
    
    



/**
     * @function addBetSlipSelection
     * @description Add selection to bet slip for specific grid
     */
addBetSlipSelection: (state, action) => {
      const { column, eventNumber, gridId, prediction } = action.payload;

      if (state.betSlip.grids[gridId]) {
        state.betSlip.grids[gridId].selections[eventNumber] = {
          column,
          eventNumber,
          prediction
        };

        // Update completion status for this grid
        state.betSlip.grids[gridId].isComplete = checkGridCompletion(state.betSlip.grids[gridId]);

        // Update overall bet slip validation
        updateBetSlipValidation(state);
      }
    },
    
    






/**
     * @function clearTotoSelections
     * @description Clear all selections or selections for a specific draw
     */
clearTotoSelections: (state, action) => {
      if (action.payload?.drawId) {
        // Clear selections for specific draw
        const drawId = action.payload.drawId;
        delete state.selectionsByDraw[drawId];

        // If it's the currently selected draw, also clear preview selections and betslip lines
        if (state.selectedDrawId === drawId) {
          state.previewSelections = {};
          // Clear multi-line betslip lines
          if (state.isMultiLineMode) {
            state.betslipLines = state.betslipLines.map(line => ({
              ...line,
              selections: {},
            }));
          }
        }
      } else {
        // Clear all selections for all draws
        state.previewSelections = {};
        state.selectionsByDraw = {};
        // Clear multi-line betslip lines
        if (state.isMultiLineMode) {
          state.betslipLines = state.betslipLines.map(line => ({
            ...line,
            selections: {},
          }));
        }
      }
    },
    
    








/**
     * @function clearDrawFinancials
     * @description Clear financial data for a specific draw or all draws
     */
clearDrawFinancials: (state, action) => {
      if (action.payload?.drawId) {
        delete state.drawFinancials[action.payload.drawId];
      } else {
        state.drawFinancials = {};
      }
    },
    
    







/**
     * @function resetTotoState
     * @description Reset the entire TOTO state to initial values
     */
resetTotoState: () => getInitialState(),
    
    







/**
     * @function clearBetSlip
     * @description Clear bet slip completely
     */
clearBetSlip: (state) => {
      state.betSlip = getInitialState().betSlip;
    },
    
    







/**
     * @function saveCurrentSelections
     * @description Explicitly save current selections to the selected draw
     */
saveCurrentSelections: (state) => {
      if (state.selectedDrawId && Object.keys(state.previewSelections).length > 0) {
        state.selectionsByDraw[state.selectedDrawId] = { ...state.previewSelections };
      }
    },
    
    






/**
     * @function clearBetSlipGrid
     * @description Clear selections for a specific grid
     */
clearBetSlipGrid: (state, action) => {
      const { gridId } = action.payload;

      if (state.betSlip.grids[gridId]) {
        state.betSlip.grids[gridId].selections = {};
        state.betSlip.grids[gridId].isComplete = false;

        // Update overall bet slip validation
        updateBetSlipValidation(state);
      }
    },
    
    






/**
     * @function setSelectedDraw
     * @description Set the currently selected draw and restore its selections
     */
setSelectedDraw: (state, action) => {
      const { drawId, gameId } = action.payload;

      // Save current selections to the previous draw if there was one
      if (state.selectedDrawId && Object.keys(state.previewSelections).length > 0) {
        state.selectionsByDraw[state.selectedDrawId] = { ...state.previewSelections };
      }

      // Set new selected draw
      state.selectedDrawId = drawId;
      state.selectedGameId = gameId;

      // Restore selections for the new draw
      state.previewSelections = state.selectionsByDraw[drawId] || {};
    },
    
    






/**
     * @function initializeBetSlip
     * @description Initialize bet slip for a specific draw and game
     */
initializeBetSlip: (state, action) => {
      const { currencyCode, drawId, gameId, grids } = action.payload;

      state.betSlip = {
        currencyCode,
        drawId,
        gameId,
        grids: {},
        isValid: false,
        totalStake: null,
        validationErrors: [],
      };

      // Initialize grid states
      grids.forEach(grid => {
        state.betSlip.grids[grid.id] = {
          gridId: grid.id,
          eventCount: grid.eventCount,
          gridName: grid.gridName,
          gridNumber: grid.gridNumber,
          isComplete: false,
          mandatory: grid.mandatory,
          prizePercentage: grid.prizePercentage,
          selections: {},
        };
      });
    },
    
    





/**
     * @function setTotoSelection
     * @description Set or update selections for an event in a specific grid and column
     */
setTotoSelection: (state, action) => {
      const { columnKey, eventId, gridId, selections } = action.payload;
      if (!state.previewSelections[eventId]) {
        state.previewSelections[eventId] = {};
      }
      if (!state.previewSelections[eventId][gridId]) {
        state.previewSelections[eventId][gridId] = {};
      }
      state.previewSelections[eventId][gridId][columnKey] = selections;
    },
    
    





/**
     * @function removeBetSlipSelection
     * @description Remove selection from bet slip for specific grid
     */
removeBetSlipSelection: (state, action) => {
      const { eventNumber, gridId } = action.payload;

      if (state.betSlip.grids[gridId]) {
        delete state.betSlip.grids[gridId].selections[eventNumber];

        // Update completion status for this grid
        state.betSlip.grids[gridId].isComplete = checkGridCompletion(state.betSlip.grids[gridId]);

        // Update overall bet slip validation
        updateBetSlipValidation(state);
      }
    },
    
    





/**
     * @function toggleTotoSelection
     * @description Toggle a selection for an event in a specific grid and column
     */
toggleTotoSelection: (state, action) => {
      const {
        eventId,
        gridId,
        columnKey,
        value,
        maxSelections = 3,
        allowsSystemBetting = true,
        isNumberSelection = false,
        numbersToSelect
      } = action.payload;

      if (!state.previewSelections[eventId]) {
        state.previewSelections[eventId] = {};
      }
      if (!state.previewSelections[eventId][gridId]) {
        state.previewSelections[eventId][gridId] = {};
      }

      const currentSelections = state.previewSelections[eventId][gridId][columnKey] || [];
      const isSelected = currentSelections.includes(value);

      // For number selection games, check cross-event limits per grid
      if (isNumberSelection && numbersToSelect && !isSelected) {
        // Count events that currently have selections in this grid
        const eventsWithSelections = Object.keys(state.previewSelections).filter(eventIdKey => {
          const eventSelections = state.previewSelections[eventIdKey];
          const gridSelections = eventSelections[gridId] || {};
          return Object.values(gridSelections).some(selections => selections && selections.length > 0);
        });

        // If this is a new event and we've reached the limit, don't allow new selections
        const hasExistingSelections = Object.values(state.previewSelections[eventId][gridId] || {}).some(
          selections => selections && selections.length > 0
        );

        if (!hasExistingSelections && eventsWithSelections.length >= numbersToSelect) {
          // Maximum events reached - don't allow new event selections
          return;
        }
      }

      if (isSelected) {
        // Remove selection
        state.previewSelections[eventId][gridId][columnKey] = currentSelections.filter(v => v !== value);
      } else {
        // Add selection
        if (!allowsSystemBetting || maxSelections === 1) {
          // Singles only - replace existing selection
          state.previewSelections[eventId][gridId][columnKey] = [value];
        } else {
          // Multiple selections allowed
          if (currentSelections.length < maxSelections) {
            state.previewSelections[eventId][gridId][columnKey] = [...currentSelections, value];
          }
        }
      }

      // Clean up empty objects
      if (state.previewSelections[eventId][gridId][columnKey]?.length === 0) {
        delete state.previewSelections[eventId][gridId][columnKey];
      }
      if (Object.keys(state.previewSelections[eventId][gridId]).length === 0) {
        delete state.previewSelections[eventId][gridId];
      }
      if (Object.keys(state.previewSelections[eventId]).length === 0) {
        delete state.previewSelections[eventId];
      }
    },
    
    



/**
     * @function setBetSlipStake
     * @description Set total stake for bet slip
     */
setBetSlipStake: (state, action) => {
      const { currencyCode, stake } = action.payload;
      state.betSlip.totalStake = stake;
      if (currencyCode) {
        state.betSlip.currencyCode = currencyCode;
      }

      // Update validation
      updateBetSlipValidation(state);
    },
    
    
/**
     * @function setSelectedStake
     * @description Set the selected stake amount and currency
     */
setSelectedStake: (state, action) => {
      state.selectedStake = action.payload.stake;
      state.selectedCurrency = action.payload.currency;
    },

    /**
     * @function initializeLines
     * @description Initialize lines for multi-line betting mode
     * @param {Object} action.payload - { gameId, drawId, requiredLines, grids }
     */
    initializeLines: (state, action) => {
      const { gameId, drawId, requiredLines, grids } = action.payload;

      state.isMultiLineMode = requiredLines > 1;
      state.requiredLineCount = requiredLines;
      state.currentLineIndex = 0;

      // Initialize empty lines
      state.betslipLines = Array.from({ length: requiredLines }, (_, index) => ({
        lineNumber: index,
        selections: {}
      }));

      // If user has existing single-line selections, migrate to Line 0
      if (Object.keys(state.previewSelections).length > 0) {
        state.betslipLines[0].selections = { ...state.previewSelections };
      }
    },

    /**
     * @function setCurrentLine
     * @description Set the currently active line for editing
     * @param {Object} action.payload - Line index number
     */
    setCurrentLine: (state, action) => {
      const lineIndex = action.payload;
      if (lineIndex >= 0 && lineIndex < state.betslipLines.length) {
        state.currentLineIndex = lineIndex;
      }
    },

    /**
     * @function toggleSelectionForLine
     * @description Toggle a selection for a specific line
     * @param {Object} action.payload - { lineIndex, eventId, gridId, columnKey, value, maxSelections, allowsSystemBetting, isNumberSelection, numbersToSelect }
     */
    toggleSelectionForLine: (state, action) => {
      const {
        lineIndex,
        eventId,
        gridId,
        columnKey,
        value,
        maxSelections = 3,
        allowsSystemBetting = true,
        isNumberSelection = false,
        numbersToSelect
      } = action.payload;

      // Validate line index
      if (!state.betslipLines[lineIndex]) {
        console.error(`Invalid line index: ${lineIndex}`);
        return;
      }

      const line = state.betslipLines[lineIndex];

      // Initialize nested structure if needed
      if (!line.selections[eventId]) {
        line.selections[eventId] = {};
      }
      if (!line.selections[eventId][gridId]) {
        line.selections[eventId][gridId] = {};
      }

      const currentSelections = line.selections[eventId][gridId][columnKey] || [];
      const isSelected = currentSelections.includes(value);

      // For number selection games, check cross-event limits per grid
      if (isNumberSelection && numbersToSelect && !isSelected) {
        // Count events that currently have selections in this grid
        const eventsWithSelections = Object.keys(line.selections).filter(eventIdKey => {
          const eventSelections = line.selections[eventIdKey];
          const gridSelections = eventSelections[gridId] || {};
          return Object.values(gridSelections).some(selections => selections && selections.length > 0);
        });

        // If this is a new event and we've reached the limit, don't allow new selections
        const hasExistingSelections = Object.values(line.selections[eventId][gridId] || {}).some(
          selections => selections && selections.length > 0
        );

        if (!hasExistingSelections && eventsWithSelections.length >= numbersToSelect) {
          // Maximum events reached - don't allow new event selections
          return;
        }
      }

      if (isSelected) {
        // Remove selection
        line.selections[eventId][gridId][columnKey] = currentSelections.filter(v => v !== value);
      } else {
        // Add selection
        if (!allowsSystemBetting || maxSelections === 1) {
          // Singles only - replace existing selection
          line.selections[eventId][gridId][columnKey] = [value];
        } else {
          // Multiple selections allowed
          if (currentSelections.length < maxSelections) {
            line.selections[eventId][gridId][columnKey] = [...currentSelections, value];
          }
        }
      }

      // Clean up empty objects
      if (line.selections[eventId][gridId][columnKey]?.length === 0) {
        delete line.selections[eventId][gridId][columnKey];
      }
      if (Object.keys(line.selections[eventId][gridId]).length === 0) {
        delete line.selections[eventId][gridId];
      }
      if (Object.keys(line.selections[eventId]).length === 0) {
        delete line.selections[eventId];
      }
    },

    /**
     * @function clearLine
     * @description Clear all selections for a specific line
     * @param {Object} action.payload - Line index number
     */
    clearLine: (state, action) => {
      const lineIndex = action.payload;
      if (state.betslipLines[lineIndex]) {
        state.betslipLines[lineIndex].selections = {};
      }
    },

    /**
     * @function duplicateLine
     * @description Copy selections from one line to another
     * @param {Object} action.payload - { fromLine, toLine }
     */
    duplicateLine: (state, action) => {
      const { fromLine, toLine } = action.payload;
      if (state.betslipLines[fromLine] && state.betslipLines[toLine]) {
        state.betslipLines[toLine].selections = JSON.parse(
          JSON.stringify(state.betslipLines[fromLine].selections)
        );
      }
    },

    /**
     * @function randomizeLine
     * @description Generate random selections for a specific line
     * @param {Object} action.payload - { lineIndex, events, columns }
     */
    randomizeLine: (state, action) => {
      const { lineIndex, events, columns } = action.payload;
      if (!state.betslipLines[lineIndex]) return;

      const line = state.betslipLines[lineIndex];
      line.selections = {};

      // Generate random selections for each event
      events.forEach((event) => {
        // Skip postponed and cancelled events
        if (event.isPostponed || event.postponed || event.isCancelled || event.cancelled) return;

        line.selections[event.eventId] = {};

        columns.forEach((column, index) => {
          const columnKey = event.isAddon ? `addon_column_${index}` : `column_${index}`;
          const options = column.options ? column.options.map(opt => opt.code) : ["1", "X", "2"];

          // Single random selection
          const randomIndex = Math.floor(Math.random() * options.length);
          line.selections[event.eventId][column.gridId || 'default'] = {
            ...line.selections[event.eventId][column.gridId || 'default'],
            [columnKey]: [options[randomIndex]]
          };
        });
      });
    },

    /**
     * @function clearAllLines
     * @description Clear all selections from all lines
     */
    clearAllLines: (state) => {
      state.betslipLines.forEach(line => {
        line.selections = {};
      });
    },

    /**
     * @function disableMultiLineMode
     * @description Exit multi-line mode and return to single-line mode
     */
    disableMultiLineMode: (state) => {
      state.isMultiLineMode = false;
      state.requiredLineCount = 1;
      state.currentLineIndex = 0;

      // Migrate first line back to previewSelections if it has content
      if (state.betslipLines.length > 0 && Object.keys(state.betslipLines[0].selections).length > 0) {
        state.previewSelections = { ...state.betslipLines[0].selections };
      }

      state.betslipLines = [];
    },
  },
});

export const {
  addBetSlipSelection,
  clearBetSlip,
  clearBetSlipGrid,
  clearBetSubmissionError,
  clearDrawFinancials,
  clearTotoError,
  clearTotoGameDetails,
  clearTotoSelections,
  initializeBetSlip,
  removeBetSlipSelection,
  resetTotoState,
  // Bet slip actions
  saveCurrentSelections,
  setBetSlipStake,
  setSelectedDraw,
  setSelectedStake,
  setTotoSelection,
  toggleTotoSelection,
  // Multi-line actions
  initializeLines,
  setCurrentLine,
  toggleSelectionForLine,
  clearLine,
  duplicateLine,
  randomizeLine,
  clearAllLines,
  disableMultiLineMode,
} = totoSlice.actions;

const { reducer } = totoSlice;
export default reducer;